import { Clock, Info, X } from "lucide-react";
import React, { memo, useMemo, useState } from "react";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { cn } from "../../../../../lib/utils";
import { getStageInitial } from "../floorplanUtils";

interface FloorplanLegendProps {
  isDark?: boolean;
}

const FloorplanLegendComponent: React.FC<FloorplanLegendProps> = ({
  isDark = false
}) => {
  const { stages } = useDiaryContext();

  const [isOpen, setIsOpen] = useState(false);

  const legendStages = useMemo(() => {
    if (!stages) return undefined;
    return [
      {
        id: "",
        label: "Not Arrived",
        colour: "6b7280",
        walk_in_default: false
      },
      ...stages
    ];
  }, [stages]);

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "h-[46px] w-[46px] rounded-full border flex items-center justify-center transition-all hover:scale-105 backdrop-blur-xl shadow-lg",
          isDark
            ? "bg-card/80 border-border text-foreground"
            : "bg-white/80 border-slate-200 text-slate-900"
        )}
        aria-label="Show legend"
        data-legend="button"
      >
        <Info size={18} />
      </button>
    );
  }

  return (
    <div
      className={cn(
        "absolute bottom-0 left-0 rounded-xl border p-4 min-w-[200px] z-50 backdrop-blur-xl shadow-lg",
        isDark
          ? "bg-card/90 border-border/50"
          : "bg-white/90 border-slate-200/50"
      )}
      data-legend="panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Info
            size={16}
            className={isDark ? "text-slate-400" : "text-slate-500"}
          />
          <h3 className="text-sm font-black">Legend</h3>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="hover:bg-slate-100 dark:hover:bg-slate-800 rounded p-1 transition-colors"
          aria-label="Hide legend"
        >
          <X size={16} />
        </button>
      </div>

      <div className="space-y-2">
        <div
          className={`text-[10px] font-black uppercase tracking-wider ${
            isDark ? "text-muted-foreground" : "text-slate-400"
          }`}
        >
          Booking Stages
        </div>
        {legendStages?.map((stage) => (
          <div key={stage.id} className="flex items-center gap-2">
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black relative flex-shrink-0"
              style={{ backgroundColor: `#${stage.colour}` }}
              aria-hidden="true"
            >
              <div
                className="absolute inset-0 rounded-full"
                style={{ backgroundColor: "rgba(255,255,255,0.5)" }}
              />
              <span className="relative z-10 text-black">
                {getStageInitial(stage.id) ?? (
                  <Clock size={16} style={{ color: "black" }} />
                )}
              </span>
            </div>
            <span className="text-xs font-medium">
              {stage.label}
              <span
                className={`ml-1 text-[10px] ${
                  isDark ? "text-slate-400" : "text-slate-500"
                }`}
              >
                ({getStageInitial(stage.id) ?? "⏱"})
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FloorplanLegend = memo(FloorplanLegendComponent);
